import { useState, useEffect } from 'react';
import { 
  Database, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Download, 
  Copy, 
  RefreshCw, 
  Info,
  Upload,
  Eye,
  FileText,
  Image
} from 'lucide-react';
import vercelBlobService from '../services/vercelBlobService';

const VercelBlobCRUD = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [operationResults, setOperationResults] = useState([]);

  // Load files on component mount
  useEffect(() => {
    loadFiles();
    loadStoreInfo();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const result = await vercelBlobService.listStoreFiles();
      if (result.success) {
        setFiles(result.files);
        console.log(`📋 Loaded ${result.files.length} files from store`);
      } else {
        console.error('Failed to load files:', result.error);
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreInfo = async () => {
    try {
      const info = await vercelBlobService.getStoreInfo();
      if (info.success) {
        setStoreInfo(info);
      }
    } catch (error) {
      console.error('Error loading store info:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadFiles();
      return;
    }

    setLoading(true);
    try {
      const result = await vercelBlobService.searchStoreFiles(searchTerm);
      if (result.success) {
        setFiles(result.files);
        addOperationResult(`🔍 Search found ${result.files.length} files matching "${searchTerm}"`);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFile = async (filename, content) => {
    try {
      const result = await vercelBlobService.createFile(filename, content);
      if (result.success) {
        addOperationResult(`✅ Created: ${filename}`);
        loadFiles();
        loadStoreInfo();
      } else {
        addOperationResult(`❌ Create failed: ${result.error}`);
      }
    } catch (error) {
      addOperationResult(`❌ Create error: ${error.message}`);
    }
  };

  const handleReadFile = async (filename) => {
    try {
      const result = await vercelBlobService.readFile(filename);
      if (result.success) {
        addOperationResult(`📖 Read: ${filename} - Size: ${result.size} bytes`);
        // Open file in new tab
        window.open(result.url, '_blank');
      } else {
        addOperationResult(`❌ Read failed: ${result.error}`);
      }
    } catch (error) {
      addOperationResult(`❌ Read error: ${error.message}`);
    }
  };

  const handleUpdateFile = async (filename, newContent) => {
    try {
      const result = await vercelBlobService.updateFile(filename, newContent);
      if (result.success) {
        addOperationResult(`🔄 Updated: ${filename}`);
        loadFiles();
        loadStoreInfo();
      } else {
        addOperationResult(`❌ Update failed: ${result.error}`);
      }
    } catch (error) {
      addOperationResult(`❌ Update error: ${error.message}`);
    }
  };

  const handleDeleteFile = async (filename) => {
    if (!confirm(`Are you sure you want to delete ${filename}?`)) return;

    try {
      const result = await vercelBlobService.deleteFileByName(filename);
      if (result.success) {
        addOperationResult(`🗑️ Deleted: ${filename}`);
        loadFiles();
        loadStoreInfo();
      } else {
        addOperationResult(`❌ Delete failed: ${result.error}`);
      }
    } catch (error) {
      addOperationResult(`❌ Delete error: ${error.message}`);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedFiles.length === 0) return;
    if (!confirm(`Are you sure you want to delete ${selectedFiles.length} files?`)) return;

    try {
      const result = await vercelBlobService.batchDeleteFiles(selectedFiles);
      addOperationResult(`🗑️ Batch delete: ${result.successCount} succeeded, ${result.failCount} failed`);
      setSelectedFiles([]);
      loadFiles();
      loadStoreInfo();
    } catch (error) {
      addOperationResult(`❌ Batch delete error: ${error.message}`);
    }
  };

  const handleCopyFile = async (sourceFilename) => {
    const targetFilename = prompt(`Enter new filename for copy of ${sourceFilename}:`);
    if (!targetFilename) return;

    try {
      const result = await vercelBlobService.copyFile(sourceFilename, targetFilename);
      if (result.success) {
        addOperationResult(`📋 Copied: ${sourceFilename} → ${targetFilename}`);
        loadFiles();
        loadStoreInfo();
      } else {
        addOperationResult(`❌ Copy failed: ${result.error}`);
      }
    } catch (error) {
      addOperationResult(`❌ Copy error: ${error.message}`);
    }
  };

  const addOperationResult = (message) => {
    setOperationResults(prev => [
      { message, timestamp: new Date().toLocaleTimeString() },
      ...prev.slice(0, 9) // Keep last 10 results
    ]);
  };

  const toggleFileSelection = (filename) => {
    setSelectedFiles(prev => 
      prev.includes(filename) 
        ? prev.filter(f => f !== filename)
        : [...prev, filename]
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      return <Image className="w-4 h-4 text-green-600" />;
    }
    return <FileText className="w-4 h-4 text-blue-600" />;
  };

  const filteredFiles = files.filter(file =>
    file.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Database className="w-6 h-6 text-purple-600 mr-3" />
          <div>
            <h3 className="text-xl font-bold text-gray-900">Vercel Blob Store CRUD</h3>
            <p className="text-gray-600">
              Store: {vercelBlobService.storeId} | Path: {vercelBlobService.storePath}
            </p>
          </div>
        </div>
        <button
          onClick={loadFiles}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Store Information */}
      {storeInfo && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Info className="w-5 h-5 text-blue-600 mr-2" />
              <span className="font-medium">Total Files</span>
            </div>
            <p className="text-2xl font-bold text-blue-600">{storeInfo.totalFiles}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Image className="w-5 h-5 text-green-600 mr-2" />
              <span className="font-medium">Images</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{storeInfo.imageFiles}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="flex items-center">
              <Database className="w-5 h-5 text-purple-600 mr-2" />
              <span className="font-medium">Total Size</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{formatFileSize(storeInfo.totalSize)}</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-orange-600 mr-2" />
              <span className="font-medium">File Types</span>
            </div>
            <p className="text-2xl font-bold text-orange-600">{Object.keys(storeInfo.fileTypes).length}</p>
          </div>
        </div>
      )}

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search files..."
            className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Search className="w-4 h-4" />
          </button>
        </div>
        
        {selectedFiles.length > 0 && (
          <button
            onClick={handleBatchDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Selected ({selectedFiles.length})
          </button>
        )}
      </div>

      {/* Files List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedFiles.length === filteredFiles.length && filteredFiles.length > 0}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedFiles(filteredFiles.map(f => f.filename));
                } else {
                  setSelectedFiles([]);
                }
              }}
              className="mr-3"
            />
            <span className="font-medium text-gray-700">
              Files ({filteredFiles.length})
            </span>
          </div>
        </div>
        
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-2 animate-spin" />
              <p className="text-gray-600">Loading files...</p>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No files found
            </div>
          ) : (
            filteredFiles.map((file) => (
              <div key={file.filename} className="flex items-center justify-between p-4 border-b border-gray-100 hover:bg-gray-50">
                <div className="flex items-center flex-1">
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(file.filename)}
                    onChange={() => toggleFileSelection(file.filename)}
                    className="mr-3"
                  />
                  {getFileIcon(file.filename)}
                  <div className="ml-3 flex-1">
                    <p className="font-medium text-gray-900">{file.filename}</p>
                    <p className="text-sm text-gray-500">
                      {formatFileSize(file.size)} • {new Date(file.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleReadFile(file.filename)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                    title="View file"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => window.open(file.downloadUrl, '_blank')}
                    className="p-2 text-green-600 hover:bg-green-100 rounded"
                    title="Download file"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleCopyFile(file.filename)}
                    className="p-2 text-purple-600 hover:bg-purple-100 rounded"
                    title="Copy file"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteFile(file.filename)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded"
                    title="Delete file"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Operation Results */}
      {operationResults.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium text-gray-900 mb-3">Recent Operations</h4>
          <div className="bg-gray-50 rounded-lg p-4 max-h-40 overflow-y-auto">
            {operationResults.map((result, index) => (
              <div key={index} className="text-sm text-gray-700 mb-1">
                <span className="text-gray-500">{result.timestamp}</span> - {result.message}
              </div>
            ))}
          </div>
        </div>
      )}


    </div>
  );
};

export default VercelBlobCRUD;
